import { UnitMode } from "../UnitMode/UnitMode";
import { SearchDisplayMode } from "~/Search/models";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ThunkAction } from "redux-thunk";
import type { AnyAction } from "redux";
import { apiUpdateUserPreferences } from "~/Search/components/ProductResults/views/ListView/components/NewDesignToggle/apiUpdateNewDesignEnabled";
import { SELECT_SERIES_TREE_NODE, SERIES_TREE_BACK } from "~/Search/redux/reducer/seriesTreeReducer";

export interface UserPreferencesSlice {
  unitMode: UnitMode;
  searchDisplayMode: {
    old: SearchDisplayMode;
    new: SearchDisplayMode;
  };
  useNewDesign: boolean;
}

const initialState: UserPreferencesSlice = {
  unitMode: (window.SCHA.user.preferences.unit as UnitMode) ?? UnitMode.Metric,
  searchDisplayMode: {
    old: SearchDisplayMode.LIST,
    new: (window.SCHA.user.preferences.searchView as SearchDisplayMode) ?? SearchDisplayMode.LIST,
  },
  useNewDesign: window.SCHA.user.preferences.newSearchView,
};

export const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    updateUnitMode: (state, action: PayloadAction<UnitMode>) => {
      state.unitMode = action.payload;
    },
    updateNewDesign: (state, action: PayloadAction<boolean>) => {
      state.useNewDesign = action.payload;
    },
    updateSearchDisplayMode: (state, action: PayloadAction<SearchDisplayMode>) => {
      if (state.useNewDesign) {
        if (action.payload !== SearchDisplayMode.SERIES_TREE) {
          state.searchDisplayMode.new = action.payload;
        }
      } else {
        state.searchDisplayMode.old = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SELECT_SERIES_TREE_NODE, (state) => {
        state.searchDisplayMode.old = SearchDisplayMode.TABULAR;
      })
      .addCase(SERIES_TREE_BACK, (state) => {
        state.searchDisplayMode.old = SearchDisplayMode.SERIES_TREE;
      });
  },
});

const { updateUnitMode, updateNewDesign, updateSearchDisplayMode } = userPreferencesSlice.actions;

export function changeUnitModeThunk(newMode: UnitMode): ThunkAction<void, UserPreferencesSlice, unknown, AnyAction> {
  return (dispatch, getState) => {
    apiUpdateUserPreferences({ unit: newMode });
    dispatch(updateUnitMode(newMode));
  };
}

export function changeNewDesignThunk(
  useNewDesign: boolean,
): ThunkAction<void, UserPreferencesSlice, unknown, AnyAction> {
  return (dispatch, getState) => {
    apiUpdateUserPreferences({ useNewDesign });
    dispatch(updateNewDesign(useNewDesign));
  };
}

export function changeDisplayModeThunk(
  displayMode: SearchDisplayMode,
): ThunkAction<void, UserPreferencesSlice, unknown, AnyAction> {
  return (dispatch, getState) => {
    const isNewDesign = getState().useNewDesign;
    if (isNewDesign && displayMode !== SearchDisplayMode.SERIES_TREE) {
      apiUpdateUserPreferences({ view: displayMode });
    }
    dispatch(updateSearchDisplayMode(displayMode));
  };
}

export default userPreferencesSlice.reducer;
